import { JSX, useEffect, useRef, useState } from 'react';
import { Text } from '@connect-x-app/hybrid-ui';
import { COLORS } from '@connect-x-app/shared-ui';
import { ADMIN_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Icon, IconWithTooltip } from '@connect-x-app/web-ui';
import { flexRender, Row, Table } from '@tanstack/react-table';
import classNames from 'classnames';

import { AddButton } from '../addButton/AddButton';
import { DeleteButton } from '../deleteButton/DeleteButton';

import './TableLayout.scss';

type Props<T> = {
  addButtonAction?: () => void;
  addButtonLabel?: string;
  className?: string;
  deleteButtonAction?: () => void;
  deleteButtonDisabled?: boolean;
  deleteButtonLabel?: string;
  emptyState?: () => JSX.Element;
  hasProportionalColumns?: boolean;
  infoTableText?: string;
  isAddButtonPresent?: boolean;
  isCollapsableTable?: boolean;
  isDeleteButtonPresent?: boolean;
  noRowsMessage?: string;
  onEndReached?: () => void;
  onRowClicked?: (row: Row<T>) => void;
  rowClickAllowed?: boolean;
  table: Table<T>;
  titleText?: string;
  titleTooltip?: string;
};

type HeaderRowProps = {
  addButtonAction: () => void;
  addButtonLabel?: string;
  isAddButtonPresent: boolean;
  isCollapsableTable?: boolean;
  isCollapsed: boolean;
  onToggle: () => void;
  titleText?: string;
  titleTooltip?: string;
};

const HeaderRow = ({
  addButtonAction,
  addButtonLabel,
  isAddButtonPresent,
  isCollapsableTable,
  isCollapsed,
  onToggle,
  titleText,
  titleTooltip,
}: HeaderRowProps) => (
  <div
    className={classNames('table-header', { 'table-header--collapsable': isCollapsableTable })}
    onClick={isCollapsableTable ? onToggle : undefined}
    role="presentation"
  >
    <div className="table-title-wrapper">
      {titleText?.length ? (
        <Text as="h3" className="table-title" color={COLORS.zulu.$10} size="XL" weight="regular">
          {titleText}
        </Text>
      ) : null}
      {titleTooltip ? (
        <IconWithTooltip colorName={COLORS.zulu.$08} iconName="circularInfoOutline" label={titleTooltip} size={2} />
      ) : null}
    </div>
    <div className="table-header-actions" onClick={event => event.stopPropagation()} role="presentation">
      {isAddButtonPresent ? <AddButton key="add-button" label={addButtonLabel} onClick={addButtonAction} /> : null}
      {isCollapsableTable ? (
        <Icon
          colorName={COLORS.zulu.$08}
          label={titleText}
          name={isCollapsed ? 'directionalChevrondownTemplateOutline' : 'directionalChevronupTemplateOutline'}
          onClick={onToggle}
          size={2.4}
          testId={ADMIN_TEST_IDS.shared.collapseToggle}
        />
      ) : null}
    </div>
  </div>
);

export const TableLayout = <T,>({
  addButtonAction = () => {},
  addButtonLabel,
  className,
  deleteButtonAction = () => {},
  deleteButtonDisabled = false,
  deleteButtonLabel,
  emptyState: EmptyState,
  hasProportionalColumns = false,
  infoTableText,
  isAddButtonPresent = false,
  isCollapsableTable,
  isDeleteButtonPresent = false,
  noRowsMessage,
  onEndReached,
  onRowClicked = () => {},
  rowClickAllowed = false,
  table,
  titleText,
  titleTooltip,
}: Props<T>) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!onEndReached) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          onEndReached();
        }
      },
      { threshold: 1.0 },
    );

    if (bottomRef.current) {
      observer.observe(bottomRef.current);
    }

    return () => observer.disconnect();
  }, [isCollapsed, onEndReached]);

  const getColumnWidth = (size: number) => (hasProportionalColumns ? `${(size / table.getTotalSize()) * 100}%` : size);

  const hasNoRows = table.getRowModel()?.rows.length === 0;

  if (hasNoRows && !noRowsMessage) {
    return EmptyState ? <EmptyState /> : null;
  }

  const handleRowClick = (row: Row<T>) => {
    if (rowClickAllowed) {
      onRowClicked(row);
    }
  };

  const onToggle = () => setIsCollapsed(collapsedValue => !collapsedValue);
  const hasHeaderRow = !!titleText?.length || isAddButtonPresent || !!isCollapsableTable;

  return (
    <div className={classNames('table-wrapper', className)}>
      {hasHeaderRow ? (
        <HeaderRow
          addButtonAction={addButtonAction}
          addButtonLabel={addButtonLabel}
          isAddButtonPresent={isAddButtonPresent}
          isCollapsableTable={isCollapsableTable}
          isCollapsed={isCollapsed}
          onToggle={onToggle}
          titleText={titleText}
          titleTooltip={titleTooltip}
        />
      ) : null}
      {infoTableText ? (
        <Text as="span" color={COLORS.zulu.$04} size="SM">
          {infoTableText}
        </Text>
      ) : null}
      {!isCollapsed && isDeleteButtonPresent ? (
        <div className="table-actions">
          <DeleteButton
            disabled={deleteButtonDisabled}
            label={deleteButtonLabel}
            onClick={deleteButtonAction}
            testId={ADMIN_TEST_IDS.shared.delete}
          />
        </div>
      ) : null}
      {!isCollapsed ? (
        <div className="scrollable-table">
          <table className={classNames({ 'table--proportional-columns': hasProportionalColumns })}>
            <thead>
              {table.getHeaderGroups()?.map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers?.map(header => (
                    <th key={header.id} style={{ width: getColumnWidth(header.getSize()) }}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {hasNoRows ? (
                <tr>
                  <td className="table-no-rows" colSpan={table.getHeaderGroups()?.[0]?.headers.length ?? 1}>
                    <Text as="span" color={COLORS.zulu.$08} size="SM">
                      {noRowsMessage}
                    </Text>
                  </td>
                </tr>
              ) : (
                table.getRowModel()?.rows.map(row => (
                  <tr
                    className={classNames({ 'clickable-row': rowClickAllowed })}
                    key={row.id}
                    onClick={() => handleRowClick(row)}
                  >
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="bottom-anchor" ref={bottomRef} />
        </div>
      ) : (
        <div className="table-collapsed-line" />
      )}
    </div>
  );
};
